// Redux slice for Guide/Lineup Editor
import type { LineupWithMetadata } from '@/stores/api/hub-content-api'

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import {
  channelLineupToGroupsWithMetadata,
  generateId,
  renumberChannelsInGroup,
  smartRenumberGroup,
  autoPopulateGroupCategories,
  renumberChannelsGapAware
} from './guideHelpers'
import type { Channel, ChannelGroup, GuideState } from './guideTypes'

/**
 * Helper function to update change tracking lists based on comparison with original state
 * This ensures channels are only marked as changed if they differ from their original state
 */
function updateChangeTracking(state: GuideState, channelId: string) {
  const isNewlyAdded = state.addedChannelIds.includes(channelId)

  // Don't track changes for newly added channels
  if (isNewlyAdded) {
    return
  }

  // Find original and current channel
  const originalChannel = state.originalGroups
    .flatMap((g) => g.channels)
    .find((c) => c.id === channelId)
  const currentChannel = state.groups
    .flatMap((g) => g.channels)
    .find((c) => c.id === channelId)

  if (!originalChannel || !currentChannel) {
    return
  }

  // Check if properties changed (including logicalChannel for position/renumbering changes)
  const propsToCheck: (keyof Channel)[] = [
    'displayName',
    'channelName',
    'channelIcon',
    'channelId',
    'ipAddress',
    'ipPort',
    'major',
    'minor',
    'isBrandVideo',
    'title',
    'description',
    'tuneType',
    'category',
    'startChannel',
    'logicalChannel' // Track channel number changes (renumbering and moves)
  ]

  const propertiesChanged = propsToCheck.some((key) => {
    return originalChannel[key] !== currentChannel[key]
  })

  const isInModifiedList = state.modifiedChannelIds.includes(channelId)

  if (propertiesChanged && !isInModifiedList) {
    state.modifiedChannelIds.push(channelId)
  } else if (!propertiesChanged && isInModifiedList) {
    state.modifiedChannelIds = state.modifiedChannelIds.filter(
      (id) => id !== channelId
    )
  }
}

/**
 * Helper to recalculate isDirty flag based on actual changes
 */
function recalculateIsDirty(state: GuideState) {
  state.isDirty =
    state.modifiedChannelIds.length > 0 || state.addedChannelIds.length > 0
}

/**
 * Helper to sort groups by startNumber (null values at end)
 */
function sortGroupsByStartNumber(groups: ChannelGroup[]) {
  groups.sort((a, b) => {
    if (a.startNumber === null && b.startNumber === null) return 0
    if (a.startNumber === null) return 1
    if (b.startNumber === null) return -1
    return a.startNumber - b.startNumber
  })
}

/**
 * Helper to recalculate endNumber for a group based on its channels
 */
function recalculateGroupEndNumber(group: ChannelGroup) {
  if (group.channels.length === 0) {
    // If no channels, reset to startNumber
    group.endNumber = group.startNumber
  } else {
    const maxLogicalChannel = Math.max(
      ...group.channels.map((ch) => ch.logicalChannel)
    )
    // Only expand endNumber if needed, never shrink (preserve user's explicit range)
    if (group.endNumber === null || maxLogicalChannel > group.endNumber) {
      group.endNumber = maxLogicalChannel
    }
  }
}

const initialState: GuideState = {
  // Current lineup metadata
  currentLineupName: null,
  currentLineupComment: null,
  isDirty: false,

  // Channel groups (frontend-only structure)
  groups: [],
  originalGroups: [],

  // UI state
  selectedChannelId: null,
  selectedChannelIds: [],
  draggedChannelId: null,
  expandedGroupIds: [],
  searchSidebarCollapsed: false,

  // Search state
  searchQuery: '',
  searchPage: 1,
  searchLimit: 30,
  lineupSearchQuery: '',

  // Change tracking
  modifiedChannelIds: [],
  addedChannelIds: []
}

const guideSlice = createSlice({
  name: 'guide',
  initialState,
  reducers: {
    // ===== Lineup Actions =====

    loadLineup(
      state,
      action: PayloadAction<{ lineup: LineupWithMetadata; name: string }>
    ) {
      const { lineup, name } = action.payload
      // Convert backend format (with metadata) to frontend groups
      // Uses explicit metadata.groups if available, otherwise falls back to gap detection
      let groups = channelLineupToGroupsWithMetadata(
        lineup.lineup,
        lineup.metadata
      )
      // Auto-populate group categories if all channels in a group share the same category
      groups = autoPopulateGroupCategories(groups)
      state.groups = groups
      // Store deep copy of original state for change detection
      state.originalGroups = JSON.parse(JSON.stringify(groups))
      state.currentLineupName = name
      state.currentLineupComment = lineup.metadata.comment || null
      state.isDirty = false
      state.selectedChannelId = null
      // Expand all groups by default
      state.expandedGroupIds = state.groups.map((g) => g.id)
      // Clear change tracking and selection
      state.modifiedChannelIds = []
      state.addedChannelIds = []
      state.selectedChannelIds = []
    },

    createNewLineup(
      state,
      action: PayloadAction<{ name: string; comment?: string }>
    ) {
      const initialGroup: ChannelGroup = {
        id: generateId(),
        startNumber: 1,
        endNumber: 1,
        channels: []
      }

      state.groups = [initialGroup]
      state.originalGroups = []
      state.currentLineupName = `channelLineup_${action.payload.name}`
      state.currentLineupComment = action.payload.comment || null
      state.isDirty = false
      state.selectedChannelId = null
      state.expandedGroupIds = [initialGroup.id]
      // Clear change tracking and selection
      state.modifiedChannelIds = []
      state.addedChannelIds = []
      state.selectedChannelIds = []
    },

    markDirty(state) {
      state.isDirty = true
    },

    clearDirty(state) {
      state.isDirty = false
      // Update original snapshot to current state
      state.originalGroups = JSON.parse(JSON.stringify(state.groups))
      // Clear change tracking when saving
      state.modifiedChannelIds = []
      state.addedChannelIds = []
    },

    setLineupComment(state, action: PayloadAction<string>) {
      state.currentLineupComment = action.payload
      state.isDirty = true
    },

    // ===== Group Actions =====

    addGroup(
      state,
      action: PayloadAction<{
        startNumber: number
        endNumber?: number
        category?: string
        index?: number
      }>
    ) {
      const { startNumber, endNumber, category, index } = action.payload
      const newGroup: ChannelGroup = {
        id: generateId(),
        startNumber,
        endNumber: endNumber ?? startNumber, // Default to startNumber if not provided
        channels: [],
        category
      }

      if (index !== undefined) {
        state.groups.splice(index, 0, newGroup)
      } else {
        state.groups.push(newGroup)
      }

      // Auto-reorder groups after adding
      sortGroupsByStartNumber(state.groups)

      state.expandedGroupIds.push(newGroup.id)
      state.isDirty = true
    },

    removeGroup(
      state,
      action: PayloadAction<{ groupId: string; mergeDirection: 'up' | 'down' }>
    ) {
      const { groupId, mergeDirection } = action.payload

      // Prevent deletion of the last group - a lineup must always have at least one group
      if (state.groups.length <= 1) {
        return
      }

      const groupIndex = state.groups.findIndex((g) => g.id === groupId)

      if (groupIndex !== -1) {
        const group = state.groups[groupIndex]

        // Validate merge direction
        const canMergeUp = groupIndex > 0
        const canMergeDown = groupIndex < state.groups.length - 1

        if (mergeDirection === 'up' && !canMergeUp) return
        if (mergeDirection === 'down' && !canMergeDown) return

        // If removing a group with channels, merge them into target group
        if (group.channels.length > 0 && state.groups.length > 1) {
          const targetIndex =
            mergeDirection === 'up' ? groupIndex - 1 : groupIndex + 1
          const targetGroup = state.groups[targetIndex]

          // Apply target group's category to merged channels
          group.channels.forEach((ch) => {
            ch.category = targetGroup.category
            updateChangeTracking(state, ch.id)
          })

          // Add channels to target group
          if (mergeDirection === 'up') {
            // Add to end of previous group
            targetGroup.channels.push(...group.channels)
          } else {
            // Add to beginning of next group
            targetGroup.channels.unshift(...group.channels)
            // Update startNumber to the deleted group's start since those channels come first now
            if (group.startNumber !== null) {
              targetGroup.startNumber = group.startNumber
            }
          }

          // Renumber target group gap-aware to preserve intentional gaps
          if (targetGroup.startNumber !== null) {
            // Start renumbering from where merged channels begin
            const mergeStartIndex =
              mergeDirection === 'up'
                ? targetGroup.channels.length - group.channels.length
                : 0
            targetGroup.channels = renumberChannelsGapAware(
              targetGroup.channels,
              mergeStartIndex
            )
            // Recalculate endNumber after renumbering
            recalculateGroupEndNumber(targetGroup)
          }
        }

        state.groups.splice(groupIndex, 1)
        state.expandedGroupIds = state.expandedGroupIds.filter(
          (id) => id !== groupId
        )
        state.isDirty = true
      }
    },

    updateGroupStartNumber(
      state,
      action: PayloadAction<{ groupId: string; startNumber: number }>
    ) {
      const { groupId, startNumber } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group) {
        group.startNumber = startNumber
        // Don't automatically renumber channels - preserve gaps
        // User can use smart renumbering if they want to renumber

        // Auto-reorder groups after start number change
        sortGroupsByStartNumber(state.groups)

        state.isDirty = true
      }
    },

    updateGroupEndNumber(
      state,
      action: PayloadAction<{ groupId: string; endNumber: number }>
    ) {
      const { groupId, endNumber } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group) {
        // Validate endNumber is at least as large as startNumber
        if (group.startNumber !== null && endNumber < group.startNumber) {
          // Don't update if end would be less than start
          return
        }
        group.endNumber = endNumber
        state.isDirty = true
      }
    },

    splitGroup(
      state,
      action: PayloadAction<{
        groupId: string
        channelIndex: number
        newStartNumber: number
      }>
    ) {
      const { groupId, channelIndex, newStartNumber } = action.payload
      const groupIndex = state.groups.findIndex((g) => g.id === groupId)

      if (groupIndex !== -1 && channelIndex > 0) {
        const group = state.groups[groupIndex]

        // Split channels
        const channelsBeforeSplit = group.channels.slice(0, channelIndex)
        const channelsAfterSplit = group.channels.slice(channelIndex)

        // Update original group
        group.channels = channelsBeforeSplit
        // Recalculate endNumber for first group
        recalculateGroupEndNumber(group)

        // Create new group with remaining channels
        const newGroup: ChannelGroup = {
          id: generateId(),
          startNumber: newStartNumber,
          endNumber: newStartNumber, // Will be recalculated below
          channels: renumberChannelsInGroup(channelsAfterSplit, newStartNumber)
        }
        // Recalculate endNumber for new group
        recalculateGroupEndNumber(newGroup)

        // Insert new group after current group
        state.groups.splice(groupIndex + 1, 0, newGroup)
        state.expandedGroupIds.push(newGroup.id)
        state.isDirty = true
      }
    },

    // Replace the full set of expanded groups. Used by the Accordion's
    // `onValueChange` callback which hands back the next open set as one
    // array rather than per-id toggles.
    setExpandedGroupIds(state, action: PayloadAction<{ groupIds: string[] }>) {
      state.expandedGroupIds = action.payload.groupIds
    },

    updateGroupCategory(
      state,
      action: PayloadAction<{ groupId: string; category: string | undefined }>
    ) {
      const { groupId, category } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group) {
        group.category = category
        // Apply category to ALL existing channels in group
        group.channels.forEach((channel) => {
          channel.category = category
          // Update change tracking based on comparison with original state
          updateChangeTracking(state, channel.id)
        })
        recalculateIsDirty(state)
      }
    },

    // ===== Channel Actions =====

    addChannelToGroup(
      state,
      action: PayloadAction<{
        groupId: string
        channel: Omit<Channel, 'id' | 'logicalChannel'>
        index?: number
      }>
    ) {
      const { groupId, channel, index } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group) {
        const newChannel = {
          ...channel,
          id: generateId(),
          logicalChannel: 0 // Will be renumbered
        } as Channel

        // Inherit group category if it exists
        if (group.category) {
          newChannel.category = group.category
        }

        const insertIndex = index !== undefined ? index : group.channels.length
        group.channels.splice(insertIndex, 0, newChannel)

        // Apply smart renumbering
        if (group.startNumber !== null) {
          const { channels, endNumber } = smartRenumberGroup(
            group.channels,
            group.startNumber,
            insertIndex
          )
          group.channels = channels
          // Only expand endNumber if needed, never shrink (preserve user's explicit range)
          if (group.endNumber === null || endNumber > group.endNumber) {
            group.endNumber = endNumber
          }
        }

        // Track as added
        if (!state.addedChannelIds.includes(newChannel.id)) {
          state.addedChannelIds.push(newChannel.id)
        }

        state.isDirty = true
      }
    },

    removeChannel(state, action: PayloadAction<{ channelId: string }>) {
      const { channelId } = action.payload

      for (const group of state.groups) {
        const channelIndex = group.channels.findIndex((c) => c.id === channelId)

        if (channelIndex !== -1) {
          group.channels.splice(channelIndex, 1)

          // Don't renumber - just recalculate endNumber to maintain group bounds
          if (group.startNumber !== null) {
            recalculateGroupEndNumber(group)
          }

          // Remove empty groups
          if (group.channels.length === 0) {
            state.groups = state.groups.filter((g) => g.id !== group.id)
          }

          state.isDirty = true
          break
        }
      }

      // Remove from all tracking arrays
      state.modifiedChannelIds = state.modifiedChannelIds.filter(
        (id) => id !== channelId
      )
      state.addedChannelIds = state.addedChannelIds.filter(
        (id) => id !== channelId
      )

      if (state.selectedChannelId === channelId) {
        state.selectedChannelId = null
      }
    },

    duplicateChannel(
      state,
      action: PayloadAction<{
        channelId: string
        groupId: string
        index: number
      }>
    ) {
      const { channelId, groupId, index } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group) {
        const sourceChannel = group.channels.find((c) => c.id === channelId)

        if (sourceChannel) {
          // Create a copy of the channel with a new ID
          const duplicatedChannel = {
            ...sourceChannel,
            id: generateId(),
            logicalChannel: 0, // Will be renumbered
            startChannel: false // Don't copy start channel status
          }

          // Insert after the original channel
          const insertIndex = index + 1
          group.channels.splice(insertIndex, 0, duplicatedChannel)

          // Apply smart renumbering
          if (group.startNumber !== null) {
            const { channels, endNumber } = smartRenumberGroup(
              group.channels,
              group.startNumber,
              insertIndex
            )
            group.channels = channels
            if (group.endNumber === null || endNumber > group.endNumber) {
              group.endNumber = endNumber
            }
          }

          // Track as added
          if (!state.addedChannelIds.includes(duplicatedChannel.id)) {
            state.addedChannelIds.push(duplicatedChannel.id)
          }

          state.isDirty = true
        }
      }
    },

    moveChannel(
      state,
      action: PayloadAction<{
        channelId: string
        fromGroupId: string
        toGroupId: string
        toIndex: number
      }>
    ) {
      const { channelId, fromGroupId, toGroupId, toIndex } = action.payload
      const fromGroup = state.groups.find((g) => g.id === fromGroupId)
      const toGroup = state.groups.find((g) => g.id === toGroupId)

      if (fromGroup && toGroup) {
        const channelIndex = fromGroup.channels.findIndex(
          (c) => c.id === channelId
        )

        if (channelIndex !== -1) {
          // Remove from source group
          const [channel] = fromGroup.channels.splice(channelIndex, 1)

          // If moving to different group, inherit that group's category
          if (fromGroupId !== toGroupId && toGroup.category) {
            channel.category = toGroup.category
          }

          // Add to destination group
          toGroup.channels.splice(toIndex, 0, channel)

          // Don't renumber source group - just recalculate endNumber
          if (fromGroupId !== toGroupId && fromGroup.startNumber !== null) {
            recalculateGroupEndNumber(fromGroup)
          }

          // Apply smart renumbering to destination group
          if (toGroup.startNumber !== null) {
            const { channels, endNumber } = smartRenumberGroup(
              toGroup.channels,
              toGroup.startNumber,
              toIndex
            )
            toGroup.channels = channels
            // Only expand endNumber if needed, never shrink (preserve user's explicit range)
            if (toGroup.endNumber === null || endNumber > toGroup.endNumber) {
              toGroup.endNumber = endNumber
            }
          }

          // Remove empty source group if different from destination
          if (fromGroupId !== toGroupId && fromGroup.channels.length === 0) {
            state.groups = state.groups.filter((g) => g.id !== fromGroupId)
          }

          // Update change tracking based on comparison with original state
          updateChangeTracking(state, channelId)
          recalculateIsDirty(state)
        }
      }
    },

    moveMultipleChannels(
      state,
      action: PayloadAction<{
        channelIds: string[]
        toGroupId: string
        toIndex: number
      }>
    ) {
      const { channelIds, toGroupId, toIndex } = action.payload
      const toGroup = state.groups.find((g) => g.id === toGroupId)

      if (!toGroup) return

      // 1. Collect all channels with their source group info and original indices
      const channelsToMove: Array<{
        channel: Channel
        sourceGroupId: string
        originalIndex: number
      }> = []
      for (const channelId of channelIds) {
        for (const group of state.groups) {
          const channelIndex = group.channels.findIndex(
            (c) => c.id === channelId
          )
          if (channelIndex !== -1) {
            const channel = group.channels[channelIndex]
            channelsToMove.push({
              channel,
              sourceGroupId: group.id,
              originalIndex: channelIndex
            })
            break
          }
        }
      }

      // 2. Calculate adjusted destination index for same-group moves
      let adjustedToIndex = toIndex
      if (
        channelsToMove.length > 0 &&
        channelsToMove[0].sourceGroupId === toGroupId
      ) {
        // Count how many channels being moved are before the destination index
        const channelsBeforeDestination = channelsToMove.filter(
          (item) => item.originalIndex < toIndex
        ).length
        adjustedToIndex = toIndex - channelsBeforeDestination
      }

      // 3. Remove all channels from source groups (reverse order to preserve indices)
      for (let i = channelsToMove.length - 1; i >= 0; i--) {
        const { channel, sourceGroupId } = channelsToMove[i]
        const sourceGroup = state.groups.find((g) => g.id === sourceGroupId)
        if (sourceGroup) {
          const index = sourceGroup.channels.findIndex(
            (c) => c.id === channel.id
          )
          if (index !== -1) {
            sourceGroup.channels.splice(index, 1)
          }
        }
      }

      // 4. Insert all channels at adjusted destination (maintain relative order)
      for (let i = 0; i < channelsToMove.length; i++) {
        const { channel, sourceGroupId } = channelsToMove[i]

        // If moving to different group, inherit that group's category
        if (sourceGroupId !== toGroupId && toGroup.category) {
          channel.category = toGroup.category
        }

        toGroup.channels.splice(adjustedToIndex + i, 0, channel)
      }

      // 5. Renumber affected groups with smart renumbering to preserve gaps
      const affectedGroupIds = new Set([
        ...channelsToMove.map((c) => c.sourceGroupId),
        toGroupId
      ])

      for (const groupId of affectedGroupIds) {
        const group = state.groups.find((g) => g.id === groupId)
        if (group && group.startNumber !== null) {
          if (groupId === toGroupId) {
            // Destination group: use smart renumbering to preserve gaps
            // Determine the starting logical number for the inserted channels
            let startingNumber: number
            if (adjustedToIndex === 0) {
              startingNumber = group.startNumber
            } else {
              const prevChannel = group.channels[adjustedToIndex - 1]
              startingNumber =
                (prevChannel?.logicalChannel ??
                  group.startNumber + adjustedToIndex - 1) + 1
            }

            // Renumber the moved channels sequentially
            for (let i = 0; i < channelsToMove.length; i++) {
              const channel = group.channels[adjustedToIndex + i]
              if (channel) {
                channel.logicalChannel = startingNumber + i
              }
            }

            // Check if subsequent channels need renumbering (conflict resolution)
            const lastInsertedNumber =
              startingNumber + channelsToMove.length - 1
            let nextExpectedNumber = lastInsertedNumber + 1

            for (
              let i = adjustedToIndex + channelsToMove.length;
              i < group.channels.length;
              i++
            ) {
              const channel = group.channels[i]

              // If there's a gap, stop renumbering
              if (channel.logicalChannel > nextExpectedNumber) {
                break
              }

              // If channel number conflicts, bump it up
              if (
                channel.logicalChannel <= lastInsertedNumber ||
                channel.logicalChannel < nextExpectedNumber
              ) {
                channel.logicalChannel = nextExpectedNumber
                nextExpectedNumber++
              } else {
                // No conflict, continue from here
                nextExpectedNumber = channel.logicalChannel + 1
              }
            }

            recalculateGroupEndNumber(group)
          } else {
            // Source group: don't renumber, just recalculate endNumber
            recalculateGroupEndNumber(group)
          }
        }
      }

      // 6. Remove empty source groups
      state.groups = state.groups.filter((g) => {
        if (g.id === toGroupId) return true // Keep destination group
        return g.channels.length > 0 // Remove empty groups
      })

      // 7. Update change tracking for all moved channels
      channelsToMove.forEach(({ channel }) => {
        updateChangeTracking(state, channel.id)
      })
      recalculateIsDirty(state)

      // 8. Clear selection after move
      state.selectedChannelIds = []
    },

    reorderChannelsInGroup(
      state,
      action: PayloadAction<{
        groupId: string
        oldIndex: number
        newIndex: number
      }>
    ) {
      const { groupId, oldIndex, newIndex } = action.payload
      const group = state.groups.find((g) => g.id === groupId)

      if (group && oldIndex !== newIndex) {
        const [channel] = group.channels.splice(oldIndex, 1)
        group.channels.splice(newIndex, 0, channel)

        // Apply smart renumbering
        if (group.startNumber !== null) {
          const { channels, endNumber } = smartRenumberGroup(
            group.channels,
            group.startNumber,
            newIndex
          )
          group.channels = channels
          // Only expand endNumber if needed, never shrink (preserve user's explicit range)
          if (group.endNumber === null || endNumber > group.endNumber) {
            group.endNumber = endNumber
          }
        }

        // Update change tracking based on comparison with original state
        updateChangeTracking(state, channel.id)
        recalculateIsDirty(state)
      }
    },

    updateChannel(
      state,
      action: PayloadAction<{
        channelId: string
        updates: Partial<Omit<Channel, 'id' | 'channel_number'>>
      }>
    ) {
      const { channelId, updates } = action.payload
      const affectedChannelIds: string[] = [channelId]

      // If setting startChannel to true, unmark all other channels
      if (updates.startChannel === true) {
        state.groups.forEach((g) => {
          g.channels.forEach((ch) => {
            if (ch.id !== channelId && ch.startChannel) {
              ch.startChannel = false
              affectedChannelIds.push(ch.id)
            }
          })
        })
      }

      for (const group of state.groups) {
        const channel = group.channels.find((c) => c.id === channelId)

        if (channel) {
          Object.assign(channel, updates)
          break
        }
      }

      // Update change tracking for all affected channels
      affectedChannelIds.forEach((id) => {
        updateChangeTracking(state, id)
      })
      recalculateIsDirty(state)
    },

    renumberChannelWithStrategy(
      state,
      action: PayloadAction<{
        channelId: string
        newNumber: number
        strategy: 'renumber' | 'move' | 'leave'
      }>
    ) {
      const { channelId, newNumber, strategy } = action.payload

      // Find the group containing this channel
      let currentGroup = null
      let currentGroupId = null
      for (const group of state.groups) {
        if (group.channels.some((ch) => ch.id === channelId)) {
          currentGroup = group
          currentGroupId = group.id
          break
        }
      }

      if (!currentGroup || !currentGroupId) return

      const currentIndex = currentGroup.channels.findIndex(
        (ch) => ch.id === channelId
      )
      if (currentIndex === -1) return

      if (strategy === 'renumber') {
        // Renumber subsequent channels in their current positions
        // Update this channel
        const channel = currentGroup.channels[currentIndex]
        channel.logicalChannel = newNumber
        updateChangeTracking(state, channelId)

        // Update all channels after this one sequentially
        for (let i = currentIndex + 1; i < currentGroup.channels.length; i++) {
          const ch = currentGroup.channels[i]
          const newNum = newNumber + (i - currentIndex)
          ch.logicalChannel = newNum
          updateChangeTracking(state, ch.id)
        }

        // Recalculate group endNumber
        recalculateGroupEndNumber(currentGroup)
      } else if (strategy === 'move') {
        // Move channel to correct sorted position and renumber conflicting channels
        // Find target position where this channel should be inserted based on the new number
        let targetIndex = currentGroup.channels.findIndex(
          (ch) => ch.id !== channelId && ch.logicalChannel >= newNumber
        )

        // If no channel found with >= number, insert at end
        if (targetIndex === -1) {
          targetIndex = currentGroup.channels.length - 1
        } else if (targetIndex > currentIndex) {
          // Adjust for the removal of the current channel
          targetIndex -= 1
        }

        // Move the channel to the correct position in the array
        if (currentIndex !== targetIndex) {
          const [channel] = currentGroup.channels.splice(currentIndex, 1)
          currentGroup.channels.splice(targetIndex, 0, channel)
        }

        // Update this channel to the new number
        const channel = currentGroup.channels.find((ch) => ch.id === channelId)
        if (channel) {
          channel.logicalChannel = newNumber
          updateChangeTracking(state, channelId)
        }

        // Get all channels that need renumbering (those with numbers >= newNumber, excluding this one)
        const channelsToRenumber = currentGroup.channels
          .filter((ch) => ch.id !== channelId && ch.logicalChannel >= newNumber)
          .sort((a, b) => a.logicalChannel - b.logicalChannel)

        // Renumber them sequentially, stopping at gaps
        let nextNumber = newNumber + 1
        for (const ch of channelsToRenumber) {
          // If there's a gap (ch.logicalChannel > nextNumber), stop renumbering
          if (ch.logicalChannel > nextNumber) {
            break
          }
          ch.logicalChannel = nextNumber
          updateChangeTracking(state, ch.id)
          nextNumber++
        }

        // Recalculate group endNumber
        recalculateGroupEndNumber(currentGroup)
      } else {
        // Leave as-is (incorrect numbering) - just update this channel
        const channel = currentGroup.channels[currentIndex]
        channel.logicalChannel = newNumber
        updateChangeTracking(state, channelId)

        // Recalculate group endNumber
        recalculateGroupEndNumber(currentGroup)
      }

      recalculateIsDirty(state)
    },

    // ===== Selection Actions =====

    setSelectedChannel(state, action: PayloadAction<string | null>) {
      state.selectedChannelId = action.payload
    },

    toggleChannelSelection(
      state,
      action: PayloadAction<{ channelId: string }>
    ) {
      const { channelId } = action.payload
      const index = state.selectedChannelIds.indexOf(channelId)

      if (index !== -1) {
        // Deselect
        state.selectedChannelIds.splice(index, 1)
      } else {
        // Select
        state.selectedChannelIds.push(channelId)
      }
    },

    clearChannelSelection(state) {
      state.selectedChannelIds = []
    },

    setChannelSelection(
      state,
      action: PayloadAction<{ channelIds: string[] }>
    ) {
      state.selectedChannelIds = action.payload.channelIds
    },

    // ===== New UI Actions =====

    toggleSearchSidebar(state) {
      state.searchSidebarCollapsed = !state.searchSidebarCollapsed
    },

    setLineupSearch(state, action: PayloadAction<{ query: string }>) {
      state.lineupSearchQuery = action.payload.query
    },

    setStartChannel(state, action: PayloadAction<{ channelId: string }>) {
      const { channelId } = action.payload

      // Set the new start channel
      for (const group of state.groups) {
        const channel = group.channels.find((c) => c.id === channelId)
        if (channel) {
          // Clear all existing start channels and track which ones changed
          const affectedChannelIds: string[] = []
          state.groups.forEach((g) => {
            g.channels.forEach((ch) => {
              if (ch.startChannel) {
                ch.startChannel = false
                affectedChannelIds.push(ch.id)
              }
            })
          })

          channel.startChannel = true
          affectedChannelIds.push(channelId)

          // Update change tracking for all affected channels
          affectedChannelIds.forEach((id) => {
            updateChangeTracking(state, id)
          })
          recalculateIsDirty(state)
          break
        }
      }
    },

    clearStartChannel(state) {
      // Clear all start channels (TLX Defined) and track which ones changed
      const affectedChannelIds: string[] = []
      state.groups.forEach((g) => {
        g.channels.forEach((ch) => {
          if (ch.startChannel) {
            ch.startChannel = false
            affectedChannelIds.push(ch.id)
          }
        })
      })

      // Update change tracking for all affected channels
      affectedChannelIds.forEach((id) => {
        updateChangeTracking(state, id)
      })
      recalculateIsDirty(state)
    }
  }
})

export const {
  loadLineup,
  createNewLineup,
  markDirty,
  clearDirty,
  setLineupComment,
  addGroup,
  removeGroup,
  updateGroupStartNumber,
  updateGroupEndNumber,
  splitGroup,
  setExpandedGroupIds,
  updateGroupCategory,
  addChannelToGroup,
  removeChannel,
  duplicateChannel,
  moveChannel,
  moveMultipleChannels,
  reorderChannelsInGroup,
  updateChannel,
  renumberChannelWithStrategy,
  setSelectedChannel,
  toggleChannelSelection,
  clearChannelSelection,
  setChannelSelection,
  toggleSearchSidebar,
  setLineupSearch,
  setStartChannel,
  clearStartChannel
} = guideSlice.actions

export default guideSlice.reducer
